import { For, type JSX, Show } from 'solid-js';
import { t, tPlural } from '../i18n';
import type { ReplayDecision, ReplayDiff } from '../lib/sessionReplay';
import { StatusPill } from './StatusPill';

// Decisions-diff presenter for the replay dry-run (#811): one `.timeline` row
// per re-evaluated decision, `original → replayed`, changed rows loudly badged.
// `policy_hash` names the candidate the verdict belongs to.

const label = (d: ReplayDecision): string => d.capability ?? d.category ?? d.kind;

export function SessionReplayDiff(props: { readonly diff: ReplayDiff }): JSX.Element {
  return (
    <div class="session-replay__diff">
      <p class="timeline__meta">
        {tPlural('session_replay.changed_count', props.diff.changed_count, {
          count: props.diff.changed_count,
        })}
        {' · '}
        {t('session_replay.policy_hash')} <code>{props.diff.policy_hash}</code>
      </p>
      <Show
        when={props.diff.decisions.length > 0}
        fallback={<div class="list-empty">{t('session_replay.no_decisions')}</div>}
      >
        <ol class="timeline session-replay__decisions">
          <For each={props.diff.decisions}>
            {(d) => (
              <li
                class="timeline__item session-replay__row"
                data-changed={d.changed ? 'true' : 'false'}
              >
                <div class="timeline__summary">
                  <span class="timeline__dot" aria-hidden="true" />
                  <div>
                    <p class="timeline__title">{label(d)}</p>
                    <p class="timeline__meta">
                      <code>{d.original}</code> → <code>{d.replayed}</code>
                      <Show when={d.detail}> · {d.detail}</Show>
                    </p>
                  </div>
                  <StatusPill tone={d.changed ? 'warning' : 'neutral'}>
                    {t(d.changed ? 'session_replay.changed' : 'session_replay.unchanged')}
                  </StatusPill>
                </div>
              </li>
            )}
          </For>
        </ol>
      </Show>
    </div>
  );
}
